import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { ClientEditWrapper } from 'Components/Clients/ClientEdit/ClientEditWrapper'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import { updateClientById } from 'Reducers/Clients/actions'
import { uploadClientImage } from 'Reducers/Upload/actions'
import ClientsIcon from 'Assets/Images/icons/clients'
import { PATHS } from 'Config/Routing/paths'
import ClientForm from '../ClientForm'
import { CLIENT_FORM_MODES } from '../const'
import { normalizeStatus, sendEmailChangeClientNameToGMsAndCFO } from '../utils'
import {
  createClientTaxExemptionByState,
  deleteClientTaxExemptionByState,
  getClientAndTagsGQL,
  getClientTaxExemptionByStateGQL,
} from './gql'
import { EDDM_SIGNUP, SS_SIGNUP } from '../../../shared/constants'
import { apiXero } from '../../../Module/API/Xero'
import { getFormattedContact } from '../XeroClient/utils'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import { showReduxModal } from '../../../Reducers/UI/actions'
import { authorization } from '../../../Module/Auth/Auth'
import { isProduction } from '../../../Utils/Functions'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import { getStatesTaxesForUS } from '../../../Module/graphQL'
import AddStateModal from '../TaxExemption/AddStateModal'
import { getBase64 } from '../../StatsDeliverability/UploadFile/utils'

const BreadcrumbsClientEdit = () => (
  <StyledBreadcrumbs
    icon={<ClientsIcon style={{ width: 24 }} />}
    breadcrumbs={[
      {
        label: 'Clients',
        link: `${PATHS.main.dashboard}${PATHS.dashboard.clients}`,
      },
      { label: 'Edit' },
    ]}
  />
)

const ClientEdit = ({ countries }) => {
  const match = useRouteMatch()
  const history = useHistory()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [client, setClient] = useState(null)
  const [allTags, setAllTags] = useState([])
  const [tenant, setTenant] = useState(null)
  const [isFetchedData, setIsFetchedData] = useState(false)
  const [xeroInfo, setXeroInfo] = useState({ isLoading: true })
  const [usersHashMap, setUsersHashMap] = useState({})
  const [xeroContact, setXeroContact] = useState(null)
  const [statesForUS, setStatesForUS] = useState([])
  const [isTaxExemptionEnabled, setIsTaxExemptionEnabled] = useState(false)
  const [isAddStateModalShown, setIsAddStateModalShown] = useState(false)
  const [isFileLoading, setIsFileLoading] = useState(false)
  const [
    uploadedDocumentsForUnitedStatesStates,
    setUploadedDocumentsForUnitedStatesStates,
  ] = useState([])
  const {
    params: { clientId },
  } = match

  const user = authorization.getUser()

  const isXeroAvailable = apiXero.isHaveXeroAccess()

  const fetchClientAndTags = async () => {
    const response = await getClientAndTagsGQL(clientId)
    const { data } = await response.json()
    const client = data.clients.nodes[0]
    const tags = data.getTags.edges.map(item => item.node)

    setIsTaxExemptionEnabled(client.isTaxExemptionEnabled)
    setUploadedDocumentsForUnitedStatesStates(data.getClientTaxExemptionByState)
    setClient(client)
    setAllTags(tags)

    return client
  }

  const fetchStatesForUS = async () => {
    const data = await getStatesTaxesForUS()
    setStatesForUS(data)
  }

  const refetchClientTaxExemptionByStateGQL = async () => {
    const data = await getClientTaxExemptionByStateGQL(clientId)
    setUploadedDocumentsForUnitedStatesStates(data)
  }

  const handleAddStateOpenModal = () => {
    setIsAddStateModalShown(true)
  }
  const handleAddStateCloseModal = () => {
    setIsAddStateModalShown(false)
  }

  const handleSaveTaxExemptionByState = async ({ state, file }) => {
    setIsFileLoading(true)
    try {
      const base64 = await getBase64(file)
      await createClientTaxExemptionByState({
        clientId,
        state,
        file: base64,
      })

      await refetchClientTaxExemptionByStateGQL()
    } catch (error) {
      console.error({ error })
    } finally {
      handleAddStateCloseModal()
      setIsFileLoading(false)
    }
  }

  const handleDeleteTaxExemptionByState = async item => {
    dispatch(
      showReduxModal({
        title: `Delete Tax Exemption?`,
        children: (
          <div style={{ textAlign: 'center' }}>
            Are you sure to delete Tax Exemption for {item.name}
          </div>
        ),
        confirmLabel: 'Confirm',
        onConfirm: async () => {
          await deleteClientTaxExemptionByState(item.id)
          await refetchClientTaxExemptionByStateGQL()
        },
      }),
    )
  }

  const fetchTenant = async client => {
    const result = await apiXero.getXeroTenant({
      includeAvailableAccount: true,
      includeCurrencies: true,
    })
    if (result && result.isAuthorized && result.isConnected) {
      if (client) {
        const contactData = await apiXero.getXeroContact({
          tenantId: result.tenantId,
          contactName: client.name,
          contactId: clientId,
        })

        if (contactData.isExist) {
          const contact = contactData.contact
          setXeroInfo({
            isLoading: false,
            entity: result.name,
            contact: contact.name,
          })
          setXeroContact(contact)

          const contactPersons = {
            ...contact.contactPersons.reduce((acc, person, index) => {
              acc[index + 1] = {
                firstName: {
                  value: person.firstName,
                  error: false,
                  required: false,
                },
                lastName: {
                  value: person.lastName,
                  error: false,
                  required: false,
                },
                email: {
                  value: person.emailAddress,
                  error: false,
                  required: false,
                },
                includeInEmails: {
                  value: person.includeInEmails,
                  error: false,
                },
              }

              return acc
            }, {}),
          }

          setUsersHashMap(contactPersons)
        } else {
          if (contactData.closestContact) {
            setXeroInfo({
              isLoading: false,
              entity: result.name,
              contact: null,
              closestContact: contactData.closestContact,
            })
          } else {
            setXeroInfo({
              isLoading: false,
              entity: result.name,
              contact: null,
              closestContact: null,
            })
          }
        }
        setTenant(result)
      }
    } else {
      setXeroInfo({
        isLoading: false,
        entity: null,
        contact: null,
        closestContact: null,
      })
    }
  }

  const fetchAllData = async () => {
    setLoading(true)
    const client = await fetchClientAndTags()
    //countryId === 14 is US
    if (client.countryId === '14') {
      await fetchStatesForUS()
    }

    try {
      await fetchTenant(client)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ err })
      setXeroInfo({ isLoading: false, error: 'Something went wrong' })
    } finally {
      setLoading(false)
      setIsFetchedData(true)
    }
  }

  useEffect(() => {
    if (!isFetchedData) {
      fetchAllData()
    }
  }, [])

  const navigateBack = () => {
    history.push(
      `${PATHS.main.dashboard}${PATHS.dashboard.clients}/${clientId}`,
    )
  }

  //TODO NEED TO OPTIMISE
  const updateXeroContact = async ({ xeroValues, oldName, newName }) => {
    const formattedXeroValues = Object.values(xeroValues).reduce(
      (acc, item) => {
        const withoutXero = item.name.replace('xero', '')
        const newKey = `${withoutXero
          .charAt(0)
          .toLowerCase()}${withoutXero.slice(1)}`
        acc[newKey] = item.value
        return acc
      },
      {},
    )

    const contact = getFormattedContact(
      { ...formattedXeroValues, trackingCategoryName: client.name },
      {
        usersHashMap,
        contactId: xeroContact.contactID,
        clientId,
        clientName: newName,
      },
    )

    await apiXero.updateXeroContact({
      tenantId: tenant.tenantId,
      contact,
      isNeedToUpdateTracking: oldName !== newName,
      oldName,
      clientName: newName,
    })
  }

  const createXeroContact = async ({ xeroValues, client }) => {
    const formattedXeroValues = Object.values(xeroValues).reduce(
      (acc, item) => {
        const withoutXero = item.name.replace('xero', '')
        const newKey = `${withoutXero
          .charAt(0)
          .toLowerCase()}${withoutXero.slice(1)}`
        acc[newKey] = item.value
        return acc
      },
      {},
    )
    const currentCounty = countries.find(
      country => country.id == client.countryId,
    )

    const contact = getFormattedContact(
      {
        ...formattedXeroValues,
        country: currentCounty.name,
        trackingCategoryName: client.name,
      },
      {
        usersHashMap,
        clientId: client.id,
        clientName: client.name,
      },
    )

    await apiXero.createXeroContact({
      contact,
      tenantId: tenant.tenantId,
      clientName: client.name,
    })
  }

  const handleSubmit = async (values, isValid, logo) => {
    if (values.name.value !== client.name && !tenant && isProduction()) {
      dispatch(
        showReduxModal({
          title: `Change Client Name?`,
          children: (
            <div style={{ textAlign: 'center' }}>
              Are you sure to change Client name without Xero Connection
              <br />
              We notify your GM and CFO
            </div>
          ),
          confirmLabel: 'Confirm',
          onConfirm: async () => {
            await handleClientEditFormSubmit(values, isValid, logo)
          },
        }),
      )
    } else {
      await handleClientEditFormSubmit(values, isValid, logo)
    }
  }

  const handleClientEditFormSubmit = async (
    {
      name,
      code,
      status,
      tags,
      country,
      industry,
      industryGroup,
      eddmOnly,
      apiAccessEnabled,
      isSelfServeEnabled,
      isAffiliate,
      isSkipCheckoutEnabled,
      sftpEnabled,
      addressVerificationEnabled,
      addressVerificationDailyBudget,
      addressVerificationMonthlyBudget,
      ...xeroValues
    },
    isValid,
    logo,
  ) => {
    if (isValid) {
      setLoading(true)
      const payload = {
        name: name.value,
        status: normalizeStatus(status.value),
        apiAccessEnabled: apiAccessEnabled.value,
        isSelfServeEnabled: Boolean(isSelfServeEnabled.value),
        isTaxExemptionEnabled,
        sftpEnabled: Boolean(sftpEnabled.value),
      }

      if (isSkipCheckoutEnabled) {
        payload.isSkipCheckoutEnabled = Boolean(isSkipCheckoutEnabled.value)
      }

      if (isAffiliate) {
        payload.isAffiliate = Boolean(isAffiliate.value)
      }

      //countryId === 14 is US - address verification is only for US clients (ADM)
      if (
        addressVerificationEnabled &&
        (client.countryId === '14' || client.countryId === 14)
      ) {
        payload.addressVerificationEnabled = Boolean(
          addressVerificationEnabled.value,
        )

        // Add daily and monthly budget limits (convert dollars to cents)
        // Default to $50 daily and $200 monthly if not provided
        if (
          addressVerificationDailyBudget &&
          addressVerificationDailyBudget.value !== undefined &&
          addressVerificationDailyBudget.value !== ''
        ) {
          payload.addressVerificationDailyBudget = Math.round(
            parseFloat(addressVerificationDailyBudget.value) * 100,
          )
        } else {
          // Apply default: $50 = 5000 cents
          payload.addressVerificationDailyBudget = 5000
        }

        if (
          addressVerificationMonthlyBudget &&
          addressVerificationMonthlyBudget.value !== undefined &&
          addressVerificationMonthlyBudget.value !== ''
        ) {
          payload.addressVerificationMonthlyBudget = Math.round(
            parseFloat(addressVerificationMonthlyBudget.value) * 100,
          )
        } else {
          // Apply default: $200 = 20000 cents
          payload.addressVerificationMonthlyBudget = 20000
        }
      }

      if (isSelfServeEnabled.value && eddmOnly) {
        if (eddmOnly.value && client.createdBy !== EDDM_SIGNUP) {
          payload.createdBy = EDDM_SIGNUP
        } else if (!eddmOnly.value && client.createdBy === EDDM_SIGNUP) {
          payload.createdBy = SS_SIGNUP
        }
      }

      if (isSelfServeEnabled.value && client.createdBy === EDDM_SIGNUP) {
        payload.createdBy = null
      }

      if (country && country.value) {
        payload.countryId = country.value
      }

      if (industry && industry.value) {
        payload.industry = industry.value
      }

      if (industryGroup && industryGroup.value) {
        payload.industryGroup = industryGroup.value
      }

      const callbackNavigateBack = {
        onSuccess: async () => {
          if (isProduction()) {
            if (tenant && isXeroAvailable) {
              if (xeroContact) {
                await updateXeroContact({
                  xeroValues,
                  newName: name.value,
                  oldName: client.name,
                })
              } else {
                await createXeroContact({
                  xeroValues,
                  client: {
                    id: clientId,
                    name: payload.name,
                    countryId: payload.countryId || client.countryId,
                  },
                })
              }
            } else {
              if (name.value !== client.name) {
                const country = countries.find(
                  country => country.id === client.countryId,
                )

                await sendEmailChangeClientNameToGMsAndCFO({
                  newName: name.value,
                  client,
                  country,
                  user,
                })
              }
            }
          }
          navigateBack()
        },
      }

      const handleUpdateLogo = ({ clientId, logo }) => {
        const callbackUpdateClient = {
          onSuccess: imageUrl => {
            dispatch(
              updateClientById(callbackNavigateBack, clientId, {
                logo: imageUrl,
              }),
            )
          },
        }
        dispatch(uploadClientImage(clientId, logo, callbackUpdateClient))
      }

      if (logo) {
        const callbackUpdateLogo = {
          onSuccess: () => handleUpdateLogo({ clientId, logo }),
        }
        dispatch(updateClientById(callbackUpdateLogo, clientId, payload))
      } else {
        dispatch(updateClientById(callbackNavigateBack, clientId, payload))
      }

      setLoading(false)
    }
  }

  return (
    <LayoutDashboard>
      <HideWhenBlocked>
        <ClientEditWrapper>
          <BreadcrumbsClientEdit />
          <AddStateModal
            statesForUS={statesForUS}
            handleClose={handleAddStateCloseModal}
            modalShow={isAddStateModalShown}
            handleSave={handleSaveTaxExemptionByState}
            isLoading={isFileLoading}
          />
          {loading ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: '0',
                left: '0',
              }}
            >
              <LoadingIndicator />
            </div>
          ) : null}
          {client && !loading ? (
            <ClientForm
              clientId={clientId}
              client={client}
              countries={countries}
              handleSubmit={handleSubmit}
              mode={CLIENT_FORM_MODES.EDIT}
              buttonsDisabled={loading}
              handleCancel={navigateBack}
              defaultValues={client}
              tags={allTags}
              country={countries.find(
                country => country.id === client.countryId,
              )}
              xeroInfo={xeroInfo}
              usersHashMap={usersHashMap}
              setUsersHashMap={setUsersHashMap}
              xeroContact={xeroContact}
              tenant={tenant}
              uploadedDocumentsForUnitedStatesStates={
                uploadedDocumentsForUnitedStatesStates
              }
              isTaxExemptionEnabled={isTaxExemptionEnabled}
              setIsTaxExemptionEnabled={setIsTaxExemptionEnabled}
              handleAddStateOpenModal={handleAddStateOpenModal}
              handleDeleteTaxExemptionByState={handleDeleteTaxExemptionByState}
            />
          ) : null}
        </ClientEditWrapper>
      </HideWhenBlocked>
    </LayoutDashboard>
  )
}

export default ClientEdit
